import {
  CreatedAtDateTimeRange,
  GetPurchaseOrdersByBarcodeQueryParams,
  GetPurchaseOrdersForEuRelabelQueryParams,
  GetPurchaseOrdersForRelabelCaseBuilderQueryParams,
  GetPurchaseOrdersQueryParams,
  GetActiveAntsPurchaseOrdersQueryParams,
  GetTransferOrdersQueryParams,
  GetTransitPurchaseOrdersQueryParams,
  GetUsersQueryParams,
  GetVendorsQueryParams,
  GetWarehouseCheckInsQueryParams,
  GetClaimsQueryParams,
  ISOString,
  IssueStatus,
  POCompletionStatus,
  RelabelCasePaginationOptions,
  PalletPaginationOptions,
  ShipmentPaginationOptions,
  Role,
  UserRole
} from "src/types";

const AUTHORISATION_KEY = "authorisation",
  PURCHASE_ORDER_KEY = "PurchaseOrder",
  PURCHASE_ORDER_IDS_KEY = "PurchaseOrderIds",
  PAGINATED_PURCHASE_ORDERS_KEY = "PaginatedPurchaseOrders",
  PAGINATED_PURCHASE_ORDERS_BY_BARCODE_KEY = "PaginatedPurchaseOrdersByBarcode",
  PAGINATED_PURCHASE_ORDERS_FOR_EU_RELABEL_KEY = "PaginatedPurchaseOrdersForEuRelabel",
  PAGINATED_PURCHASE_ORDERS_FOR_CASE_BUILDER_KEY = "PaginatedPurchaseOrdersForRelabelCaseBuilder",
  PAGINATED_TRANSIT_PURCHASE_ORDERS_KEY = "PaginatedTransitPurchaseOrders",
  PAGINATED_USERS_KEY = "PaginatedUsers",
  PAGINATED_VENDORS_KEY = "PaginatedVendors",
  VENDOR_KEY = "Vendor",
  RECEIVING_DISCREPANCY_KEY = "ReceivingDiscrepancy",
  UNKNOWN_ARRIVAL_KEY = "UnknownArrival",
  USER_SETTINGS_KEY = "UserSettings",
  USER_VENDORS_ACTIVE_PO_IDS_KEY = "UserVendorsActivePoIds",
  VENDOR_NAMES_KEY = "VendorNames",
  WAREHOUSE_CHECK_IN_KEY = "WarehouseCheckIn",
  WAREHOUSE_ROUTING_CHECK_INS_KEY = "WarehouseRoutingCheckIns",
  PAGINATED_WAREHOUSE_CHECK_INS_KEY = "PaginatedWarehouseCheckIns",
  PAGINATED_CLAIMS_KEY = "PaginatedClaims",
  CLAIM_KEY = "Claim",
  PAGINATED_RELABEL_CASES_KEY = "PaginatedRelabelCases",
  RELABEL_CASE_STATS_KEY = "RelabelCaseStats",
  PAGINATED_PALLETS_KEY = "PaginatedPallets",
  PALLET_STATS_KEY = "PalletStats",
  COMPLETE_CASES_KEY = "CompleteCases",
  PAGINATED_SHIPMENTS_KEY = "PaginatedShipments",
  SHIPMENT_STATS_KEY = "ShipmentStats",
  SHIPMENT_JOB_KEY = "ShipmentJob",
  COMPLETE_PALLETS_KEY = "CompletePallets",
  PALLET_LOCATIONS_KEY = "PalletLocations",
  BUILDING_PALLETS_KEY = "BuildingPallets",
  PAGINATED_TRANSFER_ORDERS_KEY = "PaginatedTransferOrders",
  TRANSFER_ORDER_KEY = "TransferOrder",
  TRANSFER_ORDER_EU_RELABEL_CHECK_INS_KEY = "TransferOrderEuRelabelCheckIns",
  RELABEL_CASES_BY_TRANSFER_ORDER_KEY = "RelabelCasesByTransferOrder",
  PAGINATED_ACTIVE_ANTS_PURCHASE_ORDERS_KEY = "PaginatedActiveAntsPurchaseOrders",
  ACTIVE_ANTS_PURCHASE_ORDER_KEY = "ActiveAntsPurchaseOrder",
  PAGINATED_ACTIVE_ANTS_WAREHOUSE_CHECK_INS_KEY = "PaginatedActiveAntsWarehouseCheckIns",
  ACTIVE_ANTS_WAREHOUSE_CHECK_IN_KEY = "ActiveAntsWarehouseCheckIn",
  ACTIVE_ANTS_RECEIVING_DISCREPANCY_KEY = "ActiveAntsReceivingDiscrepancy";

export const queryKeys = {
  authorisation: (roleRequired?: Role | Role[], emailVerificationRequired?: boolean) => [
    AUTHORISATION_KEY,
    roleRequired,
    emailVerificationRequired
  ],

  purchaseOrder: (purchaseOrderId: string, completionStatus?: POCompletionStatus) =>
    completionStatus ? [PURCHASE_ORDER_KEY, purchaseOrderId, completionStatus] : [PURCHASE_ORDER_KEY, purchaseOrderId],

  purchaseOrderIds: (
    completionStatus: POCompletionStatus | POCompletionStatus[],
    dateTimeRange?: CreatedAtDateTimeRange
  ) =>
    dateTimeRange
      ? [PURCHASE_ORDER_IDS_KEY, completionStatus, dateTimeRange]
      : [PURCHASE_ORDER_IDS_KEY, completionStatus],

  paginatedPurchaseOrders: (queryParams?: GetPurchaseOrdersQueryParams) =>
    queryParams ? [PAGINATED_PURCHASE_ORDERS_KEY, queryParams] : [PAGINATED_PURCHASE_ORDERS_KEY],

  paginatedPurchaseOrdersByBarcode: (queryParams?: GetPurchaseOrdersByBarcodeQueryParams) =>
    queryParams ? [PAGINATED_PURCHASE_ORDERS_BY_BARCODE_KEY, queryParams] : [PAGINATED_PURCHASE_ORDERS_BY_BARCODE_KEY],

  paginatedPurchaseOrdersForEuRelabel: (queryParams?: GetPurchaseOrdersForEuRelabelQueryParams) =>
    queryParams
      ? [PAGINATED_PURCHASE_ORDERS_FOR_EU_RELABEL_KEY, queryParams]
      : [PAGINATED_PURCHASE_ORDERS_FOR_EU_RELABEL_KEY],

  paginatedPurchaseOrdersForRelabelCaseBuilder: (queryParams?: GetPurchaseOrdersForRelabelCaseBuilderQueryParams) =>
    queryParams
      ? [PAGINATED_PURCHASE_ORDERS_FOR_CASE_BUILDER_KEY, queryParams]
      : [PAGINATED_PURCHASE_ORDERS_FOR_CASE_BUILDER_KEY],

  paginatedTransitPurchaseOrders: (queryParams?: GetTransitPurchaseOrdersQueryParams) =>
    queryParams ? [PAGINATED_TRANSIT_PURCHASE_ORDERS_KEY, queryParams] : [PAGINATED_TRANSIT_PURCHASE_ORDERS_KEY],

  paginatedUsers: (queryParams?: GetUsersQueryParams) =>
    queryParams ? [PAGINATED_USERS_KEY, queryParams] : [PAGINATED_USERS_KEY],

  paginatedVendors: (queryParams?: GetVendorsQueryParams) =>
    queryParams ? [PAGINATED_VENDORS_KEY, queryParams] : [PAGINATED_VENDORS_KEY],

  vendorById: (vendorId?: string) => (vendorId ? [VENDOR_KEY, vendorId] : [VENDOR_KEY, "unknown"]),

  receivingDiscrepancy: (purchaseOrderId: string, receivingDiscrepancyStatus?: IssueStatus) =>
    receivingDiscrepancyStatus
      ? [RECEIVING_DISCREPANCY_KEY, purchaseOrderId, receivingDiscrepancyStatus]
      : [RECEIVING_DISCREPANCY_KEY, purchaseOrderId],

  unknownArrival: (warehouseCheckInId: string, unknownArrivalStatus?: IssueStatus) =>
    unknownArrivalStatus
      ? [UNKNOWN_ARRIVAL_KEY, warehouseCheckInId, unknownArrivalStatus]
      : [UNKNOWN_ARRIVAL_KEY, warehouseCheckInId],

  userSettings: () => [USER_SETTINGS_KEY],

  userVendorsActivePOIds: (userRole: UserRole) => [USER_VENDORS_ACTIVE_PO_IDS_KEY, userRole],

  vendorNames: () => [VENDOR_NAMES_KEY],

  warehouseCheckIn: (warehouseCheckInId: string, timestamp: ISOString) =>
    timestamp ? [WAREHOUSE_CHECK_IN_KEY, warehouseCheckInId, timestamp] : [WAREHOUSE_CHECK_IN_KEY, warehouseCheckInId],

  warehouseRoutingCheckIns: (purchaseOrderId?: string) =>
    purchaseOrderId ? [WAREHOUSE_ROUTING_CHECK_INS_KEY, purchaseOrderId] : [WAREHOUSE_ROUTING_CHECK_INS_KEY],

  paginatedWarehouseCheckIns: (queryParams?: GetWarehouseCheckInsQueryParams) =>
    queryParams ? [PAGINATED_WAREHOUSE_CHECK_INS_KEY, queryParams] : [PAGINATED_WAREHOUSE_CHECK_INS_KEY],

  paginatedActiveAntsWarehouseCheckIns: (queryParams?: GetWarehouseCheckInsQueryParams) =>
    queryParams
      ? [PAGINATED_ACTIVE_ANTS_WAREHOUSE_CHECK_INS_KEY, queryParams]
      : [PAGINATED_ACTIVE_ANTS_WAREHOUSE_CHECK_INS_KEY],

  activeAntsWarehouseCheckIn: (warehouseCheckInId: string, timestamp: ISOString) =>
    timestamp
      ? [ACTIVE_ANTS_WAREHOUSE_CHECK_IN_KEY, warehouseCheckInId, timestamp]
      : [ACTIVE_ANTS_WAREHOUSE_CHECK_IN_KEY, warehouseCheckInId],

  activeAntsReceivingDiscrepancy: (activeAntsPurchaseOrderId: string, receivingDiscrepancyStatus?: IssueStatus) =>
    receivingDiscrepancyStatus
      ? [ACTIVE_ANTS_RECEIVING_DISCREPANCY_KEY, activeAntsPurchaseOrderId, receivingDiscrepancyStatus]
      : [ACTIVE_ANTS_RECEIVING_DISCREPANCY_KEY, activeAntsPurchaseOrderId],

  paginatedClaims: (queryParams?: GetClaimsQueryParams) =>
    queryParams ? [PAGINATED_CLAIMS_KEY, queryParams] : [PAGINATED_CLAIMS_KEY],

  claim: (claimId: string) => [CLAIM_KEY, claimId],

  paginatedRelabelCases: (options?: RelabelCasePaginationOptions) =>
    options ? [PAGINATED_RELABEL_CASES_KEY, options] : [PAGINATED_RELABEL_CASES_KEY],

  relabelCaseStats: () => [RELABEL_CASE_STATS_KEY],

  paginatedPallets: (options?: PalletPaginationOptions) =>
    options ? [PAGINATED_PALLETS_KEY, options] : [PAGINATED_PALLETS_KEY],

  palletStats: () => [PALLET_STATS_KEY],

  completeCases: (options?: RelabelCasePaginationOptions) =>
    options ? [COMPLETE_CASES_KEY, options] : [COMPLETE_CASES_KEY],

  paginatedShipments: (options?: ShipmentPaginationOptions) =>
    options ? [PAGINATED_SHIPMENTS_KEY, options] : [PAGINATED_SHIPMENTS_KEY],

  shipmentStats: () => [SHIPMENT_STATS_KEY],

  shipmentJob: (jobId?: string) => (jobId ? [SHIPMENT_JOB_KEY, jobId] : [SHIPMENT_JOB_KEY, "unknown"]),

  completePallets: (options?: PalletPaginationOptions) =>
    options ? [COMPLETE_PALLETS_KEY, options] : [COMPLETE_PALLETS_KEY],

  palletLocations: () => [PALLET_LOCATIONS_KEY],

  buildingPallets: () => [BUILDING_PALLETS_KEY],

  paginatedTransferOrders: (queryParams?: GetTransferOrdersQueryParams) =>
    queryParams ? [PAGINATED_TRANSFER_ORDERS_KEY, queryParams] : [PAGINATED_TRANSFER_ORDERS_KEY],

  transferOrder: (transferOrderId: string) => [TRANSFER_ORDER_KEY, transferOrderId],

  transferOrderEuRelabelCheckIns: (transferOrderId: string) => [
    TRANSFER_ORDER_EU_RELABEL_CHECK_INS_KEY,
    transferOrderId
  ],

  relabelCasesByTransferOrder: (transferOrderId: string) => [RELABEL_CASES_BY_TRANSFER_ORDER_KEY, transferOrderId],

  paginatedActiveAntsPurchaseOrders: (queryParams?: GetActiveAntsPurchaseOrdersQueryParams) =>
    queryParams
      ? [PAGINATED_ACTIVE_ANTS_PURCHASE_ORDERS_KEY, queryParams]
      : [PAGINATED_ACTIVE_ANTS_PURCHASE_ORDERS_KEY],

  activeAntsPurchaseOrder: (activeAntsPurchaseOrderId: string) => [
    ACTIVE_ANTS_PURCHASE_ORDER_KEY,
    activeAntsPurchaseOrderId
  ]
};
